import { Children, Fragment, isValidElement, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';
import { useOverlayFocus } from '../../utils/useOverlayFocus';
import { useEntranceMotion } from '../../utils/useEntranceMotion';

export interface ModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
  readonly size?: 'sm' | 'md' | 'lg';
  /** Set false to disable the X close button (force a footer action). */
  readonly showCloseButton?: boolean;
  /** Set false to disable backdrop click → close. */
  readonly dismissOnBackdrop?: boolean;
  /** Set false to disable Escape → close. */
  readonly dismissOnEscape?: boolean;
  readonly className?: string;
  /** Accessible name when no visible title is supplied. */
  readonly ariaLabel?: string;
  readonly closeLabel?: string;
}

const SIZE = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
} as const;

function hasContent(children: ReactNode): boolean {
  return Children.toArray(children).some((child) => {
    if (isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment) {
      return hasContent(child.props.children);
    }
    return typeof child !== 'string' || child.trim().length > 0;
  });
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  showCloseButton = true,
  dismissOnBackdrop = true,
  dismissOnEscape = true,
  className,
  ariaLabel,
  closeLabel,
}: ModalProps) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();

  useOverlayFocus(open, dialogRef, onClose, dismissOnEscape);
  useEntranceMotion(dialogRef, open);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="presentation"
      onClick={(e) => {
        if (dismissOnBackdrop && e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={
          !title ? (ariaLabel ?? t('common:dialog.title', { defaultValue: 'Dialog' })) : undefined
        }
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn(
          'w-full rounded-xl bg-surface text-ink shadow-[0_16px_40px_-20px_rgba(0,0,0,0.20)]',
          'flex flex-col max-h-[calc(100dvh-2rem)] overflow-hidden border border-hairline-2',
          'focus:outline-none',
          SIZE[size],
          className,
        )}
      >
        {title || description || showCloseButton ? (
          <header className="flex items-start gap-3 p-5 border-b border-hairline">
            <div className="flex-1 min-w-0">
              {title ? (
                <h2 id={titleId} className="text-[18px] font-bold text-ink leading-tight">
                  {title}
                </h2>
              ) : null}
              {description ? (
                <p id={descId} className="mt-1 text-[13px] text-ink-2 leading-snug">
                  {description}
                </p>
              ) : null}
            </div>
            {showCloseButton ? (
              <button
                type="button"
                aria-label={closeLabel ?? t('common:actions.close', { defaultValue: 'Close' })}
                onClick={onClose}
                className="size-10 shrink-0 inline-flex items-center justify-center rounded-md text-ink-2 hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
              >
                <CloseIcon size={18} color="currentColor" />
              </button>
            ) : null}
          </header>
        ) : null}
        {hasContent(children) ? (
          <div className="p-5 overflow-y-auto flex-1" data-testid="modal-body">
            {children}
          </div>
        ) : null}
        {footer ? (
          <footer className="p-4 border-t border-hairline flex flex-wrap items-center justify-end gap-2 bg-surface-2/40">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
